import React, { useEffect, useState } from "react";
import { Table, Button, TablePaginationConfig, Pagination, Row, Col, Select } from "antd";
// eslint-disable-next-line import/no-extraneous-dependencies
import { GetComponentProps } from "rc-table/lib/interface";
import { AnyObject } from "../../lib/interfaces";
import { ReactComponent as VersionsHiddenIcon } from "../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../img/versions_icons/versionsShown_icon.svg";
import ToggleColumns from "./ToggleColumns";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import TablePageSelect from "../TablePageSelect/TablePageSelect";
import "./ToggleableVersionsTable.less";

interface Props {
  tableName: string;
  title?: string | React.ComponentElement<any, any>;
  columns: any[];
  setColumns: React.Dispatch<React.SetStateAction<any>>;
  settings?: { [key: string]: { name: string; value: any } };
  setSettings?: React.Dispatch<React.SetStateAction<any>>;
  tableData: any[];
  childTableColumns?: any[];
  setChildTableColumns?: React.Dispatch<React.SetStateAction<any>>;
  childTableData: any[];
  isTableLoading?: boolean;
  isChildTableLoading?: boolean;
  onOpenVersionsCallback?: (record?: any, expanded?: any) => void;
  onCloseVersionsCallback?: (record?: any, expanded?: any) => void;
  tableClassName?: string;
  childTableClassName?: string;
  childRowClassName?: (record: any, idx?: number) => string;
  rowClassName?: (record: any, idx?: number) => string;
  isPageable?: false | TablePaginationConfig;
  onRow?: GetComponentProps<any>;
  isToggleableColumns?: boolean;
  childOnRow?: GetComponentProps<any>;
  onChange?: () => void;
  style?: AnyObject;
  resetExpandOnTableDataChange?: boolean;
  editingKey?: boolean;
  scroll?: {
    x?: string | number | true | undefined;
    y?: string | number | undefined;
  } & {
    scrollToFirstRowOnChange?: boolean | undefined;
  };
  isPageSizeChangable?:
    | false
    | {
        onChange: (size: number) => void;
        value: number;
        pageSizeOptions: { [key: number]: number };
      };
  isVisibleEye?: boolean;
}

function ToggleableVersionsTable(props: Props) {
  const {
    tableName,
    title,
    columns,
    setColumns,
    settings,
    setSettings,
    tableData,
    childTableColumns,
    childTableData,
    isTableLoading,
    isChildTableLoading,
    onOpenVersionsCallback,
    onCloseVersionsCallback,
    tableClassName,
    isPageable = false,
    isPageSizeChangable = false,
    onRow,
    isToggleableColumns = true,
    setChildTableColumns,
    childTableClassName,
    rowClassName,
    childRowClassName,
    childOnRow,
    onChange = () => {},
    style,
    resetExpandOnTableDataChange,
    scroll,
    isVisibleEye = true,
    editingKey,
  } = props;
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  // Сброс expanded по изменении tableData
  useEffect(() => {
    if (resetExpandOnTableDataChange) {
      setExpandedRowKeys([]);
    }
  }, [tableData, resetExpandOnTableDataChange]);

  const isTableChanging =
    (childTableData && versionsTableUtils.getIsTableChanging(childTableData)) || editingKey;

  const expandIconColumnIndex =
    [...columns].filter((item) => (isTableChanging ? true : !item.hidden)).length - 1;

  const innerRowClassName = (record) => {
    if (childRowClassName) return childRowClassName(record);
    if (record.isCurrentVersion) {
      return `row_child row_current-version`;
    }
    if (record.isPastVersion) {
      return "row_child row_past-version";
    }
    return "row_child";
  };

  const innerTableColumns = (function fn() {
    if (!childTableColumns) return null;
    if (columns.length !== childTableColumns.length) return childTableColumns;
    const invisibleButton = (
      <Button
        className="button-icon"
        icon={<VersionsHiddenIcon />}
        type="text"
        htmlType="button"
        style={{ visibility: "hidden" }}
      />
    );
    return [
      ...childTableColumns.slice(0, childTableColumns.length - 1),
      {
        key: "eye",
        render: () => invisibleButton,
        width: versionsTableUtils.columnsWidths.eye,
      },
      ...childTableColumns.slice(childTableColumns.length - 1),
    ];
  })();

  const expandedRowRender = () => {
    if (innerTableColumns) {
      return (
        <Table
          columns={[...innerTableColumns].filter((item) => (isTableChanging ? true : !item.hidden))}
          className={`"table_no-hover child-table " ${childTableClassName || ""}`}
          dataSource={childTableData}
          rowClassName={innerRowClassName}
          loading={isChildTableLoading}
          pagination={false}
          showHeader={false}
          size="small"
          onRow={childOnRow}
        />
      );
    }
    return false;
  };

  const expandable = isVisibleEye
    ? {
        expandedRowRender,
        expandedRowKeys,
        indentSize: 0,
        expandIconColumnIndex,
        columnWidth: versionsTableUtils.columnsWidths.eye,
        expandIcon: ({ expanded, record }) => {
          if (!expanded)
            return (
              <Button
                className="button-icon"
                icon={<VersionsHiddenIcon />}
                type="text"
                htmlType="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!expanded) {
                    setExpandedRowKeys((prev) => {
                      if (prev.length > 0 && onCloseVersionsCallback) {
                        onCloseVersionsCallback(record);
                      }
                      return [record.key];
                    });
                    if (onOpenVersionsCallback !== undefined)
                      onOpenVersionsCallback(record, expanded);
                  }
                }}
              />
            );
          return (
            <Button
              className="button-icon"
              icon={<VersionsShownIcon />}
              type="text"
              htmlType="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedRowKeys([]);
                if (onCloseVersionsCallback) {
                  onCloseVersionsCallback(record, expanded);
                }
              }}
            />
          );
        },
      }
    : undefined;

  return (
    <div className="versions-table" style={{ marginTop: 10, ...style }}>
      <Row justify="space-between" align="middle" style={{ paddingBottom: 10 }}>
        <Col span={4} />
        <Col>{isPageable && <Pagination {...isPageable} size="small" />}</Col>
        <Col span={4}>
          {isPageSizeChangable && (
            <TablePageSelect
              onChange={isPageSizeChangable.onChange}
              value={isPageSizeChangable.value}
              pageSizeOptions={isPageSizeChangable.pageSizeOptions}
            />
          )}
        </Col>
      </Row>
      <Row justify="space-between" align="middle">
        <Col span={8}>{title}</Col>
        <Col span={8} style={{ textAlign: "right" }}>
          {isToggleableColumns && (
            <ToggleColumns
              columns={columns}
              setColumns={setColumns}
              settings={settings}
              setSettings={setSettings}
              tableName={tableName}
              setChildTableColumns={setChildTableColumns}
              isButtonDisabled={isTableChanging}
            />
          )}
        </Col>
      </Row>
      <Table
        columns={[...columns].filter((item) => (isTableChanging ? true : !item.hidden))}
        dataSource={tableData}
        className={tableClassName}
        rowClassName={rowClassName}
        loading={isTableLoading}
        size="small"
        onChange={onChange}
        onRow={onRow}
        pagination={false}
        scroll={scroll}
        expandable={expandable}
      />
    </div>
  );
}

export default ToggleableVersionsTable;
